import { CostPriceAnalysis, fetchCostPriceAnalysesByRestaurant } from '../../../../services/costPrice/costPriceService';
import { CustomIngredient, fetchCustomIngredientsByRestaurant } from '../../../../services/customIngredients/customIngredientsService';
import { Subrecipe, fetchSubrecipesByRestaurant } from '../../../../services/subrecipes/subrecipesService';

export interface AutomationRunSources {
  dishes: CostPriceAnalysis[];
  subrecipes: Subrecipe[];
  customIngredients: CustomIngredient[];
}

/** Follow reachable nested dishes across restaurant boundaries, including third-hop sources. */
export async function loadAutomationRunSources(restaurantId: string, localDishes: CostPriceAnalysis[], scopedLoader?: () => Promise<AutomationRunSources>): Promise<AutomationRunSources> {
  // Staff setup receives only the assigned run's explicit dependencies, never sibling libraries.
  if (scopedLoader) return scopedLoader();
  const scopes = new Map<string, AutomationRunSources>();
  const visited = new Set<string>();
  const loadScope = async (id: string) => {
    if (id === restaurantId) return { dishes: localDishes, subrecipes: [], customIngredients: [] };
    const cached = scopes.get(id);
    if (cached) return cached;
    const [dishes, subrecipes, customIngredients] = await Promise.all([
      fetchCostPriceAnalysesByRestaurant(id), fetchSubrecipesByRestaurant(id), fetchCustomIngredientsByRestaurant(id),
    ]);
    const scope = { dishes, subrecipes, customIngredients };
    scopes.set(id, scope);
    return scope;
  };
  const visit = async (dish: CostPriceAnalysis) => {
    const key = `${dish.restaurantId}:${dish.id}`;
    if (visited.has(key)) return;
    visited.add(key);
    for (const ingredient of dish.ingredients) {
      const sourceId = ingredient.sourceRestaurantId || dish.restaurantId;
      const scope = await loadScope(sourceId);
      if (ingredient.nestedCostPriceId) {
        const nested = scope.dishes.find(candidate => candidate.id === ingredient.nestedCostPriceId && candidate.restaurantId === sourceId);
        if (nested) await visit(nested);
      }
    }
  };
  for (const dish of localDishes) await visit(dish);
  return {
    dishes: Array.from(scopes.values()).flatMap(scope => scope.dishes),
    subrecipes: Array.from(scopes.values()).flatMap(scope => scope.subrecipes),
    customIngredients: Array.from(scopes.values()).flatMap(scope => scope.customIngredients),
  };
}
